import {
  ChevronDown,
  ChevronUp,
  UserRound,
} from 'lucide-react'
import { useState } from 'react'

import AnimateHeightAndOpacity from 'components/reusable/AnimateHeightAndOpacity'
import AnimateOpacity from 'components/reusable/AnimateOpacity'
import TooltipWrapper from 'components/reusable/TooltipWrapper'
import { getFormattedDate } from 'utils'
import {
  type BrokersTask,
  BrokersTaskStatus as BrokersTaskStatusEnum,
  type BrokersWorkflowStatus,
} from 'utils/web_api.schemas'
import { BrokersWorkflowStatus as BrokersWorkflowStatusEnum } from 'utils/web_api.schemas'

import TaskActionControls from './TaskActionControls'
import {
  ExecutionContextBadge,
  ExecutionMethodBadge,
  getTaskCompactStyle,
  TaskStatusBadge,
} from './WorkflowVisualIndicators'

interface TasksListProps {
  tasks: BrokersTask[]
  workflowStatus?: BrokersWorkflowStatus
  workflowId?: string
  enrollmentId?: string
  handleTaskAction?: (taskId: string, action: string) => void
  taskMutation?: { isPending: boolean }
  showActions?: boolean
}

const formatTaskType = (type: string) => {
  return type
    .replace(/Tasks::V2::/, '')
    .replace(/Task$/, '')
    .replace(/([A-Z])/g, ' $1')
    .trim()
}

export const TasksList = ({
  tasks,
  workflowStatus,
  workflowId,
  enrollmentId,
  handleTaskAction,
  taskMutation,
  showActions = true,
}: TasksListProps) => {
  const [isExpanded, setIsExpanded] = useState(
    workflowStatus !== BrokersWorkflowStatusEnum.completed
  )

  if (!tasks || tasks.length === 0) {
    return null
  }

  const sortedTasks = [...tasks].sort((a, b) => {
    const aOrder = a.sequence_order || 0
    const bOrder = b.sequence_order || 0
    return aOrder - bOrder
  })

  // Calculate task summary for collapsed state
  const taskSummary = {
    total: sortedTasks.length,
    completed: sortedTasks.filter(
      (task) => task.status === BrokersTaskStatusEnum.completed
    ).length,
    pending: sortedTasks.filter(
      (task) => task.status === BrokersTaskStatusEnum.pending
    ).length,
    failed: sortedTasks.filter(
      (task) => task.status === BrokersTaskStatusEnum.failed
    ).length,
  }

  const getSummaryText = () => {
    const parts = []
    if (taskSummary.completed > 0) {
      parts.push(`${taskSummary.completed} completed`)
    }
    if (taskSummary.pending > 0) parts.push(`${taskSummary.pending} pending`)
    if (taskSummary.failed > 0) parts.push(`${taskSummary.failed} failed`)
    return parts.join(', ')
  }

  return (
    <div className='space-y-2'>
      {/* Tasks Header with Toggle Button */}
      <div className='flex items-center justify-between'>
        <div className='flex items-center space-x-2'>
          <h5 className='font-medium text-sm text-gray-700'>Tasks:</h5>
          <AnimateOpacity isOpen={!isExpanded}>
            <span className='text-xs text-gray-500'>{getSummaryText()}</span>
          </AnimateOpacity>
        </div>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className='flex items-center space-x-1 text-xs text-gray-500 hover:text-gray-700 transition-colors'
        >
          <span>{isExpanded ? 'Collapse' : 'Expand'}</span>
          {isExpanded
            ? (
              <ChevronUp className='w-3 h-3' />
              )
            : (
              <ChevronDown className='w-3 h-3' />
              )}
        </button>
      </div>

      {/* Animated Tasks List */}
      <AnimateHeightAndOpacity isOpen={isExpanded}>
        <div className='space-y-1'>
          {sortedTasks.map((task: BrokersTask) => {
            return (
              <div
                key={task.id}
                className={`flex items-center justify-between py-2 px-3 bg-gray-50 rounded text-sm ${getTaskCompactStyle(
                  task
                )}`}
              >
                <div className='flex items-center space-x-2'>
                  <span className='font-medium'>
                    {task.display_name || formatTaskType(task.task_type || '')}
                  </span>
                  <TaskStatusBadge
                    status={task.status}
                    isExecutable={task.can_execute}
                  />
                  <ExecutionMethodBadge method={task.execution_method} />
                  <ExecutionContextBadge context={task.execution_context} />

                  {/* Processing By Broker */}
                  {task.status === BrokersTaskStatusEnum.processing &&
                    task.processed_by_broker && (
                      <TooltipWrapper tooltipText='Broker who is processing the task'>
                        <div className='flex items-center space-x-1 text-xs text-gray-400'>
                          <UserRound className='w-3 h-3' />
                          <span>{task.processed_by_broker.broker_name}</span>
                          {task.processed_at && (
                            <>
                              <span>•</span>
                              <span className='text-xs text-gray-400'>
                                {getFormattedDate(task.processed_at, {
                                  hour: 'numeric',
                                  minute: '2-digit',
                                  hour12: true,
                                })}
                              </span>
                            </>
                          )}
                        </div>
                      </TooltipWrapper>
                  )}

                  {/* Completed By Broker */}
                  {task.status === BrokersTaskStatusEnum.completed &&
                    task.completed_by_broker && (
                      <TooltipWrapper tooltipText='Broker who completed the task'>
                        <div className='flex items-center space-x-1 text-xs text-gray-400'>
                          <UserRound className='w-3 h-3' />
                          <span>{task.completed_by_broker.broker_name}</span>
                          {task.completed_at && (
                            <>
                              <span>•</span>
                              <span className='text-xs text-gray-400'>
                                {getFormattedDate(task.completed_at, {
                                  hour: 'numeric',
                                  minute: '2-digit',
                                  hour12: true,
                                })}
                              </span>
                            </>
                          )}
                        </div>
                      </TooltipWrapper>
                  )}
                </div>

                <div className='flex items-center space-x-2'>
                  {/* Task Actions for Manual Pending Tasks */}
                  {showActions &&
                    handleTaskAction &&
                    workflowId &&
                    enrollmentId && (
                      <TaskActionControls
                        task={task}
                        onTaskAction={handleTaskAction}
                        isLoading={taskMutation?.isPending || false}
                        workflowId={workflowId}
                        enrollmentId={enrollmentId}
                      />
                  )}

                  {/* Always show sequence order */}
                  {task.sequence_order && (
                    <span className='text-xs text-gray-400'>
                      #{task.sequence_order}
                    </span>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </AnimateHeightAndOpacity>
    </div>
  )
}

export default TasksList
